import {useEffect} from "react";
import {Loader} from "@mantine/core";
import {Navigate} from "react-router-dom";
import {Feature, useFeature} from "../../featureFlags/featureFlags";
import {dibAppApi} from "../../dibAppApi/dibAppApi";
import {useContentParams} from "./router";
import {ChecklistResource} from "./ChecklistResource";
import {DocumentGeneratorResource} from "./DocumentGeneratorResource";
import {ExternalFallback} from "./ExternalFallback";
import {HTMLResource} from "./HTMLResource";
import {HTMLResourceLEGACY} from "./legacy/HTMLResourceLEGACY";
import {ResourceError} from "./ResourceError";
import {StatusMessage} from "./StatusMessage";
import {ResourceType} from "./types";
import layoutClasses from "./ContentLayout.module.css";
import classes from "./ContentPage.module.css";
import {PanelProvider} from "./panel/PanelContext";
import {SliderPanel} from "./panel/SliderPanel";

export const ContentPage = () => {
    const {enabled: canViewContent, isLoading: isUserDataLoading} = useFeature(
        Feature.ViewDibContent
    );
    const {enabled: newContentDesign} = useFeature(Feature.NewContentDesign);
    const {
        resourceId,
        dokumentId,
        collectionId,
        segmentId,
        isValidTopicType,
        isPreview,
    } = useContentParams();

    const isSkipped = !resourceId || (!isPreview && !isValidTopicType);
    const {
        data: resourceTypeId,
        isLoading: isPrefetchLoading,
        isError,
        error,
    } = dibAppApi.endpoints.getResourceTypeId.useQuery(
        {topicId: resourceId ?? ""},
        {skip: isSkipped}
    );

    // Title is owned by the leaf resource components (they set
    // `<topic name> - KAI` once their data loads). ContentPage covers the
    // windows where no leaf is mounted: keep "KAI" while resolving the resource
    // type / skipped / errored, and reset on leaving any content route.
    useEffect(() => {
        if (isSkipped || isPrefetchLoading || isError) {
            document.title = "KAI";
        }
    }, [isSkipped, isPrefetchLoading, isError]);

    useEffect(() => {
        return () => {
            document.title = "KAI";
        };
    }, []);

    if (isUserDataLoading) return null;
    if (!canViewContent) return <Navigate to="/" replace />;

    const getContent = () => {
        if (isSkipped) {
            return (
                <StatusMessage
                    title="Content not found"
                    detail="The page you're looking for doesn't exist or the URL is invalid."
                />
            );
        }

        if (isPrefetchLoading) {
            return (
                <div className={layoutClasses.statusMessage}>
                    <Loader color="gneis.5" size="md" />
                </div>
            );
        }

        if (isError) {
            return <ResourceError error={error} />;
        }

        switch (resourceTypeId) {
            case ResourceType.html:
            case ResourceType.staticHtml:
            case ResourceType.skatteAbc: {
                const ResourceComponent = newContentDesign
                    ? HTMLResource
                    : HTMLResourceLEGACY;
                return (
                    <ResourceComponent
                        resourceId={resourceId!}
                        segmentId={segmentId}
                    />
                );
            }
            case ResourceType.checklist:
                return (
                    <ChecklistResource
                        resourceId={resourceId!}
                        collectionId={collectionId}
                        dokumentId={dokumentId}
                        isPreview={isPreview}
                    />
                );
            case ResourceType.documentGenerator:
                return (
                    <DocumentGeneratorResource
                        resourceId={resourceId!}
                        isPreview={isPreview}
                    />
                );
            default:
                return (
                    <ExternalFallback
                        resourceId={resourceId!}
                        segmentId={segmentId}
                    />
                );
        }
    };

    return (
        <PanelProvider>
            <div className={classes.contentPage}>{getContent()}</div>
            <SliderPanel />
        </PanelProvider>
    );
};
